/**
 * Report issue 접수 (STUDIO-SUPPORT-001 · ICD-MAIN-231).
 *
 * 사용자는 제목을 쓰지 않는다. 증상을 고르고, 목록에 없을 때만 직접 적는다.
 * 제목은 서버가 만든다 — 목록에서 제목으로 접수 창구를 가르는 규칙은 Admin 이
 * 소유하므로 클라이언트가 만들면 문구를 바꿀 때 배포가 두 곳이 된다.
 *
 * 소속과 유형도 보내지 않는다. 소속은 서버가 조회하고, 이 창구의 유형은 버그
 * 하나로 고정이다.
 */

import { apiCall } from "./base";
import { collectReportLog } from "../../utils/reportLogBuffer";

/** 화면에 보이는 순서 그대로다. 서버의 STUDIO_SYMPTOMS 와 같은 순서를 쓴다. */
export const STUDIO_REPORT_SYMPTOMS = [
  "figma_import_failed",
  "object_upload_failed",
  "export_failed",
  "project_failed_to_load",
  "changes_not_saved",
  "other",
] as const;

export type StudioReportSymptom = (typeof STUDIO_REPORT_SYMPTOMS)[number];

/** 사용자에게 보이는 라벨. Object·View R Project 는 제품 용어를 따른다. */
export const STUDIO_REPORT_SYMPTOM_LABELS: Record<StudioReportSymptom, string> = {
  figma_import_failed: "Figma import failed",
  object_upload_failed: "Object upload failed",
  export_failed: "Export failed",
  project_failed_to_load: "View R Project failed to load",
  changes_not_saved: "Changes not saved",
  other: "Other",
};

export const AVP_REPORT_SYMPTOMS = [
  "object_frozen", "scale_not_changing", "gesture_not_responding",
  "prototype_not_loading", "object_disappeared", "other",
] as const;
export type AvpReportSymptom = (typeof AVP_REPORT_SYMPTOMS)[number];
export type ReportPlatform = "studio" | "avp";
export type ReportSymptom = StudioReportSymptom | AvpReportSymptom;
export const REPORT_SYMPTOMS: Record<ReportPlatform, readonly ReportSymptom[]> = {
  studio: STUDIO_REPORT_SYMPTOMS,
  avp: AVP_REPORT_SYMPTOMS,
};
export const REPORT_SYMPTOM_LABELS: Record<ReportSymptom, string> = {
  ...STUDIO_REPORT_SYMPTOM_LABELS,
  object_frozen: "Object frozen",
  scale_not_changing: "Scale not changing",
  gesture_not_responding: "Gesture not responding",
  prototype_not_loading: "Prototype not loading",
  object_disappeared: "Object disappeared",
};

export interface StudioReportDraft {
  platform?: "studio";
  symptoms: StudioReportSymptom[];
  /** Other 를 골랐을 때만 필요하다. 사용자가 쓴 원문 그대로 보낸다. */
  description?: string | null;
}

export type ReportDraft = StudioReportDraft | {
  platform: "avp";
  symptoms: AvpReportSymptom[];
  description?: string | null;
};

export interface StudioReportResult {
  id: string;
  status: string;
  title: string;
}

/**
 * 발생 위치. 식별자를 뺀 라우트 경로만 보낸다. 프로젝트 id 나 Space id 가
 * 그대로 들어가면 값이 매번 달라져 집계가 되지 않는다.
 */
export const currentPageContext = (pathname?: string): string => {
  const path = pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  if (!path) return "/";
  const [, head] = path.split("/");
  return head ? `/${head}` : "/";
};

/** 클라이언트. `web/<앱 버전>/<브라우저-OS>` 고정 포맷이다. */
export const currentClientTag = (): string => {
  const version = process.env.REACT_APP_VERSION || "unknown";
  const agent = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const browser = /Edg\//.test(agent)
    ? "Edge"
    : /Chrome\//.test(agent)
      ? "Chrome"
      : /Safari\//.test(agent)
        ? "Safari"
        : /Firefox\//.test(agent)
          ? "Firefox"
          : "Other";
  const os = /Mac OS X|Macintosh/.test(agent)
    ? "macOS"
    : /Windows/.test(agent)
      ? "Windows"
      : /Android/.test(agent)
        ? "Android"
        : /iPhone|iPad/.test(agent)
          ? "iOS"
          : "Other";
  return `web/${version}/${browser}-${os}`.slice(0, 200);
};

export const submitStudioReport = async (
  draft: ReportDraft,
): Promise<StudioReportResult> => {
  const description = (draft.description ?? "").trim();
  const response = (await apiCall("/api/reports", {
    method: "POST",
    body: JSON.stringify({
      platform: draft.platform ?? "studio",
      symptoms: draft.symptoms,
      description: description || null,
      pageContext: currentPageContext(),
      client: currentClientTag(),
      logExcerpt: draft.platform === "avp" ? null : collectReportLog(),
    }),
  })) as StudioReportResult;
  return response;
};
